import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, type UserRef } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import { refusalText } from '../lib/reach';
import { FormPage } from '../components/FormPage';
import { useToast } from '../components/ui';
import { WorkflowBuilder, levelOf, workflowPayload, type LevelDraft } from '../components/WorkflowBuilder';
import { PROGRESS_LABEL, type WorkflowJourney } from '../components/WorkflowChain';

/** The levels a journey has not reached yet. */
const ahead = (journey: WorkflowJourney) => journey.levels.filter((l) => l.level > journey.currentLevel);

/**
 * Route: `/tasks/:id/workflow` — Edit the workflow (2026-10-15): the levels a
 * running workflow has not reached yet, changed — who is on them, how many there
 * are and in which order — from any step of it the editor can open. The levels
 * reached are shown above, as they are. Whoever started the work, a team leader
 * or an admin (`can.editWorkflow`). Work that moves on meanwhile wins: the page
 * then shows the levels ahead as they are now, for the change to be made again.
 */
export function TaskWorkflowEditPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const task = useQuery({ queryKey: ['task', id], queryFn: () => api.get<any>(`/tasks/${id}`) });
  const journey: WorkflowJourney | null = task.data?.workflow ?? null;
  const allowed = !!journey && !!task.data?.can?.editWorkflow;
  const projectId: string | null = task.data?.projectId ?? null;
  // Who may join: a project task's people are its project's (§8.4); any other's, the editor's departments'.
  const { data: people } = useQuery({
    queryKey: projectId ? ['project', projectId, 'assignees'] : ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options', { projectId }),
    enabled: !!journey,
  });

  // The levels ahead as the page found them — found again once the work has moved on to another level.
  const [draft, setDraft] = useState<{ level: number; levels: LevelDraft[] } | null>(null);
  if (journey && (!draft || draft.level !== journey.currentLevel)) {
    setDraft({ level: journey.currentLevel, levels: ahead(journey).map((l) => levelOf(l.people.map((p) => p.id))) });
  }
  const current = journey?.currentLevel ?? 0;
  const before = journey ? ahead(journey).map((l) => l.people.map((p) => p.id)) : [];
  const after = draft?.levels.map((l) => l.userIds) ?? [];
  const changed = JSON.stringify(before) !== JSON.stringify(after);
  const ready = !!draft && draft.levels.every((l) => l.userIds.length > 0);
  // Someone on them who has left since may stay — their turn is skipped — but is marked.
  const inactive = new Set(
    (journey?.levels ?? []).flatMap((l) => l.people.filter((p) => p.isActive === false).map((p) => p.id)),
  );

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/tasks/${id}/workflow`, { currentLevel: draft?.level, levels: workflowPayload(draft?.levels ?? []).levels }),
    onSuccess: () => {
      for (const queryKey of [['task', id], ['tasks'], ['kanban']]) void queryClient.invalidateQueries({ queryKey });
      toast.success(t('tasks.workflowEdit.saved'));
      navigate(`/tasks/${id}`, { replace: true });
    },
    onError: (e: Error) => {
      if (e instanceof ApiError && e.code === 'CONCURRENT_CHANGE') {
        // Handed on, finished or changed by someone else meanwhile: show it as it is now.
        void task.refetch().then(() => setDraft(null));
        toast.error(t('tasks.workflowEdit.movedOn'));
        return;
      }
      toast.error(refusalText(e, t));
    },
  });

  const names = (list: Array<UserRef | null>) => list.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');
  const reached = journey ? journey.levels.filter((l) => l.level <= journey.currentLevel) : [];
  // Who did the work on a level reached: its steps' people — or, passed over, who was on it.
  const workedBy = (l: WorkflowJourney['levels'][number]) => {
    const steps = [...l.tasks.map((s) => s.returnTo ?? s.assignee), ...(l.others ?? [])];
    return steps.length ? steps : l.people;
  };

  return (
    <FormPage
      title={t('tasks.workflowEdit.title')}
      subtitle={task.data?.title}
      backTo={`/tasks/${id}`}
      backLabel={task.data?.title ?? t('nav.tasks')}
      submitLabel={t('tasks.workflowEdit.submit')}
      submitting={save.isPending}
      disabled={!allowed || !ready || !changed}
      loading={task.isLoading}
      error={task.error}
      onRetry={() => void task.refetch()}
      onSubmit={() => save.mutate()}
      summary={
        reached.length > 0 && (
          <div>
            <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">
              {t('tasks.workflowEdit.reached')}
            </span>
            <ol className="mt-2 space-y-1.5">
              {reached.map((l) => (
                <li key={l.level} className="text-body-sm">
                  <span className="font-medium text-[var(--text-primary)]">
                    {t('workflows.levelN', { n: formatNumber(l.level, locale) })}
                  </span>
                  <span className="ms-1.5 text-caption text-muted">· {t(PROGRESS_LABEL[l.progress])}</span>
                  <span className="block break-words text-caption text-muted">{names(workedBy(l))}</span>
                </li>
              ))}
            </ol>
            <p className="mt-2 text-caption text-faint">{t('tasks.workflowEdit.reachedHint')}</p>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {task.data && !allowed && (
          <p className="rounded-md border border-warning/35 bg-warning-bg px-3 py-2 text-body-sm text-warning-fg dark:bg-warning/8 dark:text-warning-dark">
            {!journey
              ? t('tasks.workflowEdit.noWorkflow')
              : journey.status !== 'ACTIVE'
                ? t('tasks.workflowEdit.notRunning')
                : t('tasks.workflowEdit.notYours')}
          </p>
        )}
        {journey && draft && (
          <WorkflowBuilder
            levels={draft.levels}
            onChange={(levels) => setDraft({ ...draft, levels })}
            people={people ?? []}
            known={journey.levels.flatMap((l) => l.people)}
            firstLevel={current + 1}
            minLevels={0}
            legend={t('tasks.workflowEdit.ahead')}
            hint={t('tasks.workflowEdit.aheadHint')}
            flagged={inactive}
            flagLabel={t('tasks.workflowEdit.inactive')}
            flagTone="warning"
            disabled={!allowed}
            footer={
              draft.levels.length === 0 && (
                <p className="mt-3 text-caption text-muted">
                  {t('tasks.workflowEdit.none', { n: formatNumber(current, locale) })}
                </p>
              )
            }
          />
        )}
      </div>
    </FormPage>
  );
}
